import { STRING_TRUE } from '@/data/constants/catalog';
import { fulfillmentTypes } from 'shared/custom/constants/commonConstants';

/**
 * STORECONF key names for the EDD (estimated delivery date) feature.
 * See LeadTime/StoreConfInserts.md for the environment inserts and defaults.
 */
export const EDD_STORECONF_KEYS = {
	DELIVERY_CUTOFF_TIME: 'EDD_DELIVERY_CUTOFF_TIME',
	DELIVERY_AFTER_CUTOFF_DAYS: 'EDD_DELIVERY_AFTER_CUTOFF_DAYS',
	PICKUP_CUTOFF_TIME: 'EDD_PICKUP_CUTOFF_TIME',
	PICKUP_EXPRESS_DAYS: 'EDD_PICKUP_EXPRESS_DAYS',
	PICKUP_STANDARD_DAYS: 'EDD_PICKUP_STANDARD_DAYS',
	DELIVERY_EXCLUDED_WEEKDAYS: 'EDD_DELIVERY_EXCLUDED_WEEKDAYS',
	PICKUP_EXCLUDED_WEEKDAYS: 'EDD_PICKUP_EXCLUDED_WEEKDAYS',
	DELIVERY_EXCLUDED_DATES: 'EDD_DELIVERY_EXCLUDED_DATES',
	PICKUP_EXCLUDED_DATES: 'EDD_PICKUP_EXCLUDED_DATES',
	PICKUP_DROPSHIP_ENABLED: 'EDD_PICKUP_DROPSHIP_ENABLED',
	PICKUP_SPECIAL_ORDER_ENABLED: 'EDD_PICKUP_SPECIAL_ORDER_ENABLED',
	PICKUP_ONLINE_ONLY_ENABLED: 'EDD_PICKUP_ONLINE_ONLY_ENABLED',
	// PDP delivery-options story (AC4): owned by the PDP, same EDD_ mechanics.
	PICKUP_SLA_HOURS: 'EDD_PICKUP_SLA_HOURS',
	PICKUP_LATEST_READY_TIME: 'EDD_PICKUP_LATEST_READY_TIME',
	PICKUP_NEXT_DAY_READY_TIME: 'EDD_PICKUP_NEXT_DAY_READY_TIME',
	FREE_SHIPPING_THRESHOLD: 'EDD_FREE_SHIPPING_THRESHOLD',
	PICKUP_ONLY_ENABLED: 'EDD_PICKUP_ONLY_ENABLED',
	// Same Day story 163024 (AC4): its own cutoff and calendar, see `SameDayConfig`.
	SAMEDAY_ENABLED: 'EDD_SAMEDAY_ENABLED',
	SAMEDAY_CUTOFF_TIME: 'EDD_SAMEDAY_CUTOFF_TIME',
	SAMEDAY_AFTER_CUTOFF_DAYS: 'EDD_SAMEDAY_AFTER_CUTOFF_DAYS',
	SAMEDAY_EXCLUDED_DATES: 'EDD_SAMEDAY_EXCLUDED_DATES',
	SAMEDAY_EXCLUDED_WEEKDAYS: 'EDD_SAMEDAY_EXCLUDED_WEEKDAYS',
} as const;

const EDD_STORECONF_DEFAULTS = {
	[EDD_STORECONF_KEYS.DELIVERY_CUTOFF_TIME]: '18:00',
	[EDD_STORECONF_KEYS.DELIVERY_AFTER_CUTOFF_DAYS]: '1',
	[EDD_STORECONF_KEYS.PICKUP_CUTOFF_TIME]: '18:00',
	[EDD_STORECONF_KEYS.PICKUP_EXPRESS_DAYS]: '0',
	[EDD_STORECONF_KEYS.PICKUP_STANDARD_DAYS]: '1',
	[EDD_STORECONF_KEYS.DELIVERY_EXCLUDED_WEEKDAYS]: '0',
	[EDD_STORECONF_KEYS.PICKUP_EXCLUDED_WEEKDAYS]: '',
	[EDD_STORECONF_KEYS.DELIVERY_EXCLUDED_DATES]: '12-25,01-01',
	[EDD_STORECONF_KEYS.PICKUP_EXCLUDED_DATES]: '12-25,01-01',
	[EDD_STORECONF_KEYS.PICKUP_DROPSHIP_ENABLED]: STRING_TRUE,
	[EDD_STORECONF_KEYS.PICKUP_SPECIAL_ORDER_ENABLED]: STRING_TRUE,
	[EDD_STORECONF_KEYS.PICKUP_ONLINE_ONLY_ENABLED]: STRING_TRUE,
	[EDD_STORECONF_KEYS.PICKUP_SLA_HOURS]: '3',
	[EDD_STORECONF_KEYS.PICKUP_LATEST_READY_TIME]: '21:00',
	[EDD_STORECONF_KEYS.PICKUP_NEXT_DAY_READY_TIME]: '11:00',
	[EDD_STORECONF_KEYS.FREE_SHIPPING_THRESHOLD]: '799',
	[EDD_STORECONF_KEYS.PICKUP_ONLY_ENABLED]: 'false',
	[EDD_STORECONF_KEYS.SAMEDAY_ENABLED]: 'false',
	[EDD_STORECONF_KEYS.SAMEDAY_CUTOFF_TIME]: '14:00',
	[EDD_STORECONF_KEYS.SAMEDAY_AFTER_CUTOFF_DAYS]: '1',
	[EDD_STORECONF_KEYS.SAMEDAY_EXCLUDED_DATES]: '12-25,01-01',
	[EDD_STORECONF_KEYS.SAMEDAY_EXCLUDED_WEEKDAYS]: '',
} as const;

export type ExcludedDate = { month: number; day: number };

export type EddConfig = {
	deliveryCutoffTime: string;
	deliveryAfterCutoffDays: number;
	pickupCutoffTime: string;
	pickupExpressDays: number;
	pickupStandardDays: number;
	deliveryExcludedWeekdays: number[];
	pickupExcludedWeekdays: number[];
	deliveryExcludedDates: ExcludedDate[];
	pickupExcludedDates: ExcludedDate[];
	pickupDropshipEnabled: boolean;
	pickupSpecialOrderEnabled: boolean;
	pickupOnlineOnlyEnabled: boolean;
	/** PDP delivery-options story, AC4/AC7: in-store picking SLA, added to the query time. */
	pickupSlaHours: number;
	/** AC4/AC8: latest acceptable ready hour, one hour before the store closes. */
	pickupLatestReadyTime: string;
	/** AC4/AC8: ready hour shown when the resolved date is not the current day. */
	pickupNextDayReadyTime: string;
	/** AC4/AC9: home delivery is free at or above this SKU offer-price amount. */
	freeShippingThreshold: number;
	/**
	 * AC19-22: transitional switch for the pickup-only state, applied globally.
	 * Consumed by `useHomeDeliveryDate` via `isPickupOnlyEnabled` (the single
	 * resolution point AC22 refers to) -- it resolves home delivery straight to
	 * the same `not-available` state/message 159073 already defines for a
	 * `bopisOnly` product.
	 */
	pickupOnlyEnabled: boolean;
	/**
	 * Same Day story 163024 AC4-AC7: a branch of home delivery with its own cutoff
	 * and calendar, never shared with the home delivery one (AC6/AC7). `null` when
	 * `EDD_SAMEDAY_ENABLED` is off, so per AC5 none of its storeconfs is consumed.
	 */
	sameDay: SameDayConfig | null;
};

export type SameDayConfig = {
	cutoffTime: string;
	afterCutoffDays: number;
	excludedWeekdays: number[];
	excludedDates: ExcludedDate[];
};

const warnInvalid = (name: string, rawValue: string | undefined, fallback: string) =>
	// eslint-disable-next-line no-console
	console.warn(
		`[EDD] invalid or missing storeconf "${name}" (got "${rawValue}"), using default "${fallback}"`
	);

const TIME_PATTERN = /^([01]\d|2[0-3]):([0-5]\d)$/;

const readRaw = (userData: Record<string, string> | undefined, name: string): string => {
	const fallback = EDD_STORECONF_DEFAULTS[name as keyof typeof EDD_STORECONF_DEFAULTS];
	const value = userData?.[name];
	return value === undefined ? fallback : value;
};

const readTime = (userData: Record<string, string> | undefined, name: string): string => {
	const fallback = EDD_STORECONF_DEFAULTS[name as keyof typeof EDD_STORECONF_DEFAULTS];
	const value = readRaw(userData, name);
	if (!TIME_PATTERN.test(value)) {
		warnInvalid(name, value, fallback);
		return fallback;
	}
	return value;
};

const readDays = (userData: Record<string, string> | undefined, name: string): number => {
	const fallback = EDD_STORECONF_DEFAULTS[name as keyof typeof EDD_STORECONF_DEFAULTS];
	const value = readRaw(userData, name);
	const parsed = Number.parseInt(value, 10);
	if (Number.isNaN(parsed) || parsed < 0) {
		warnInvalid(name, value, fallback);
		return Number.parseInt(fallback, 10);
	}
	return parsed;
};

/**
 * Same mechanics as `readDays` (AC4: "same mechanics declared in US 158621 AC1"),
 * but for a monetary amount rather than a day count, so a decimal peso value
 * (e.g. a threshold of "799.50") is not rejected as invalid.
 */
const readAmount = (userData: Record<string, string> | undefined, name: string): number => {
	const fallback = EDD_STORECONF_DEFAULTS[name as keyof typeof EDD_STORECONF_DEFAULTS];
	const value = readRaw(userData, name);
	const parsed = Number.parseFloat(value);
	if (Number.isNaN(parsed) || parsed < 0) {
		warnInvalid(name, value, fallback);
		return Number.parseFloat(fallback);
	}
	return parsed;
};

/** Comma-separated list, ignoring blank entries and surrounding whitespace. */
const splitList = (value: string): string[] =>
	value
		.split(',')
		.map((part) => part.trim())
		.filter((part) => part.length > 0);

/** `null` signals an invalid value, so the caller can fall back per AC6. */
const parseWeekdays = (value: string): number[] | null => {
	if (!value.trim()) return [];
	const parsed = splitList(value).map((part) => Number.parseInt(part, 10));
	return parsed.some((day) => Number.isNaN(day) || day < 0 || day > 6) ? null : parsed;
};

/**
 * AC6: a list containing any invalid entry falls back to the AC1 default in full
 * rather than silently applying the entries that happened to parse — dropping one
 * excluded weekday would quietly change which days the channel promises.
 */
const readWeekdays = (userData: Record<string, string> | undefined, name: string): number[] => {
	const fallback = EDD_STORECONF_DEFAULTS[name as keyof typeof EDD_STORECONF_DEFAULTS];
	const value = readRaw(userData, name);
	const parsed = parseWeekdays(value);
	if (parsed === null) {
		warnInvalid(name, value, fallback);
		return parseWeekdays(fallback) ?? [];
	}
	return parsed;
};

const DATE_PATTERN = /^(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;

/** `null` signals an invalid value, so the caller can fall back per AC6. */
const parseExcludedDates = (value: string): ExcludedDate[] | null => {
	if (!value.trim()) return [];
	const parts = splitList(value);
	if (parts.some((part) => !DATE_PATTERN.test(part))) return null;
	return parts.map((part) => {
		const [month, day] = part.split('-').map((n) => Number.parseInt(n, 10));
		return { month, day };
	});
};

/**
 * AC6: same all-or-nothing rule as `readWeekdays` — a typo such as `12-25,01-1`
 * must not silently drop New Year from the excluded dates.
 */
const readExcludedDates = (
	userData: Record<string, string> | undefined,
	name: string
): ExcludedDate[] => {
	const fallback = EDD_STORECONF_DEFAULTS[name as keyof typeof EDD_STORECONF_DEFAULTS];
	const value = readRaw(userData, name);
	const parsed = parseExcludedDates(value);
	if (parsed === null) {
		warnInvalid(name, value, fallback);
		return parseExcludedDates(fallback) ?? [];
	}
	return parsed;
};

const STRING_FALSE = 'false';

/**
 * AC6: casing and whitespace are normalised, so `TRUE` from a storeconf edit is
 * honoured rather than silently read as `false` — matching how
 * `isProductFlagEnabled` treats the sibling per-SKU flags. Anything that is not
 * recognisably a boolean falls back to the AC1 default and is logged.
 */
const readBoolean = (userData: Record<string, string> | undefined, name: string): boolean => {
	const fallback = EDD_STORECONF_DEFAULTS[name as keyof typeof EDD_STORECONF_DEFAULTS];
	const value = readRaw(userData, name);
	const normalized = value.trim().toLowerCase();
	if (normalized === STRING_TRUE) return true;
	if (normalized === STRING_FALSE) return false;
	warnInvalid(name, value, fallback);
	return fallback === STRING_TRUE;
};

const timeToMinutes = (time: string): number => {
	const [hours, minutes] = time.split(':').map((part) => Number.parseInt(part, 10));
	return hours * 60 + minutes;
};

/**
 * Last bullet of AC4: the pickup cutoff plus the picking SLA must land at or before
 * the latest ready time (itself one hour before the store closes) — a store cannot
 * be asked to ready an order after it has closed. A violation only logs with the
 * three values; per AC4 it does not alter the collection date, which stories
 * 158621/159042 already resolve independently of this check.
 */
const warnIfPickupSlaExceedsLatestReadyTime = (
	pickupCutoffTime: string,
	pickupSlaHours: number,
	pickupLatestReadyTime: string
): void => {
	const slaEndMinutes = timeToMinutes(pickupCutoffTime) + pickupSlaHours * 60;
	if (slaEndMinutes > timeToMinutes(pickupLatestReadyTime)) {
		// eslint-disable-next-line no-console
		console.warn(
			`[EDD] EDD_PICKUP_CUTOFF_TIME (${pickupCutoffTime}) + EDD_PICKUP_SLA_HOURS ` +
				`(${pickupSlaHours}h) exceeds EDD_PICKUP_LATEST_READY_TIME (${pickupLatestReadyTime}); ` +
				`the collection date resolved by stories 158621/159042 is unaffected.`
		);
	}
};

export const getEddConfig = (userData: Record<string, string> | undefined): EddConfig => {
	const pickupCutoffTime = readTime(userData, EDD_STORECONF_KEYS.PICKUP_CUTOFF_TIME);
	const pickupSlaHours = readDays(userData, EDD_STORECONF_KEYS.PICKUP_SLA_HOURS);
	const pickupLatestReadyTime = readTime(userData, EDD_STORECONF_KEYS.PICKUP_LATEST_READY_TIME);
	warnIfPickupSlaExceedsLatestReadyTime(pickupCutoffTime, pickupSlaHours, pickupLatestReadyTime);

	return {
		deliveryCutoffTime: readTime(userData, EDD_STORECONF_KEYS.DELIVERY_CUTOFF_TIME),
		deliveryAfterCutoffDays: readDays(userData, EDD_STORECONF_KEYS.DELIVERY_AFTER_CUTOFF_DAYS),
		pickupCutoffTime,
		pickupExpressDays: readDays(userData, EDD_STORECONF_KEYS.PICKUP_EXPRESS_DAYS),
		pickupStandardDays: readDays(userData, EDD_STORECONF_KEYS.PICKUP_STANDARD_DAYS),
		deliveryExcludedWeekdays: readWeekdays(userData, EDD_STORECONF_KEYS.DELIVERY_EXCLUDED_WEEKDAYS),
		pickupExcludedWeekdays: readWeekdays(userData, EDD_STORECONF_KEYS.PICKUP_EXCLUDED_WEEKDAYS),
		deliveryExcludedDates: readExcludedDates(userData, EDD_STORECONF_KEYS.DELIVERY_EXCLUDED_DATES),
		pickupExcludedDates: readExcludedDates(userData, EDD_STORECONF_KEYS.PICKUP_EXCLUDED_DATES),
		pickupDropshipEnabled: readBoolean(userData, EDD_STORECONF_KEYS.PICKUP_DROPSHIP_ENABLED),
		pickupSpecialOrderEnabled: readBoolean(
			userData,
			EDD_STORECONF_KEYS.PICKUP_SPECIAL_ORDER_ENABLED
		),
		pickupOnlineOnlyEnabled: readBoolean(userData, EDD_STORECONF_KEYS.PICKUP_ONLINE_ONLY_ENABLED),
		pickupSlaHours,
		pickupLatestReadyTime,
		pickupNextDayReadyTime: readTime(userData, EDD_STORECONF_KEYS.PICKUP_NEXT_DAY_READY_TIME),
		freeShippingThreshold: readAmount(userData, EDD_STORECONF_KEYS.FREE_SHIPPING_THRESHOLD),
		pickupOnlyEnabled: readBoolean(userData, EDD_STORECONF_KEYS.PICKUP_ONLY_ENABLED),
		sameDay: readSameDayConfig(userData),
	};
};

/** AC5: with the master switch off the rest of the Same Day storeconfs is never read. */
const readSameDayConfig = (userData: Record<string, string> | undefined): SameDayConfig | null =>
	readBoolean(userData, EDD_STORECONF_KEYS.SAMEDAY_ENABLED)
		? {
				cutoffTime: readTime(userData, EDD_STORECONF_KEYS.SAMEDAY_CUTOFF_TIME),
				afterCutoffDays: readDays(userData, EDD_STORECONF_KEYS.SAMEDAY_AFTER_CUTOFF_DAYS),
				excludedWeekdays: readWeekdays(userData, EDD_STORECONF_KEYS.SAMEDAY_EXCLUDED_WEEKDAYS),
				excludedDates: readExcludedDates(userData, EDD_STORECONF_KEYS.SAMEDAY_EXCLUDED_DATES),
		  }
		: null;

export const BYPASSES_CUTOFF: ReadonlySet<string> = new Set([
	fulfillmentTypes.DROPSHIP,
	fulfillmentTypes.SPECIAL_ORDER,
]);
